import React from 'react';

export interface ProfileItem {
  index: string;
  label: string;
  content: React.ReactNode;
}

const linkClassName =
  'text-zinc-200 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-white transition-colors';

export const PROFILE_ITEMS: readonly ProfileItem[] = [
  {
    index: '01',
    label: 'Experience',
    content: (
      <>
        Now{' '}
        <a
          href="https://www.google.com"
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          Google
        </a>
        , Before{' '}
        <a
          href="https://worldline.com"
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          Worldline
        </a>{' '}
        and{' '}
        <a
          href="https://www.axenta.ch"
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          aXenta
        </a>
        .
      </>
    ),
  },
  {
    index: '02',
    label: 'Education',
    content: (
      <>
        BSc in Computer Science,{' '}
        <a
          href="https://www.zhaw.ch"
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          ZHAW
        </a>
        .
      </>
    ),
  },
  {
    index: '03',
    label: 'Beyond Code',
    content: 'Motorsports, Drums and Game Development.',
  },
];

export const App: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white selection:bg-white selection:text-black flex flex-col justify-between">
      {/* Top Minimalist Bar */}
      <header className="border-b border-white/12">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <span
              aria-hidden="true"
              className="w-6 h-6 bg-white text-black font-mono text-[11px] font-semibold tracking-tighter flex items-center justify-center select-none"
            >
              YS
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.22em] text-white">
              Yanick Senn
            </span>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            Zürich, Switzerland
          </div>
        </div>
      </header>

      {/* Main Content — High-Level Hero & Profile */}
      <main className="flex-1 flex flex-col justify-center max-w-4xl w-full mx-auto px-6 sm:px-12 py-20 sm:py-28 space-y-24">
        {/* 01 / About */}
        <section aria-labelledby="hero-heading" className="space-y-10">
          <div className="flex items-center gap-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">
              01 / About
            </span>
            <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
          </div>

          <div className="space-y-5">
            <h1
              id="hero-heading"
              className="text-5xl sm:text-7xl md:text-8xl font-light tracking-[-0.04em] text-white leading-[0.96]"
            >
              Yanick Senn
            </h1>
            <p className="text-xl sm:text-2xl font-normal tracking-[-0.015em] text-zinc-300 max-w-2xl">
              Software Engineer based in Zürich, Switzerland.
            </p>
          </div>

          <div className="max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed font-light pt-2">
            <p>
              I am a software engineer who likes to engineer software. Passionate about clean
              architecture, reducing technical debt, automated testing, and building open-source and
              personal projects.
            </p>
          </div>
        </section>

        {/* 02 / Profile */}
        <section aria-labelledby="profile-heading" className="space-y-10">
          <div className="flex items-center gap-4">
            <h2
              id="profile-heading"
              className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500 font-normal"
            >
              02 / Profile
            </h2>
            <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
          </div>

          <div className="divide-y divide-white/10 border-y border-white/10">
            {PROFILE_ITEMS.map((item) => (
              <article
                key={item.index}
                className="py-6 sm:py-7 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-8 items-baseline"
              >
                <div className="md:col-span-4 flex items-baseline gap-4">
                  <span className="font-mono text-xs text-zinc-500 tracking-widest">
                    {item.index}
                  </span>
                  <h3 className="text-base sm:text-lg font-medium tracking-tight text-white">
                    {item.label}
                  </h3>
                </div>
                <p className="md:col-span-8 text-sm sm:text-base text-zinc-400 leading-relaxed font-light">
                  {item.content}
                </p>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* Minimalist Footer */}
      <footer className="border-t border-white/12">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 h-14 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          <span>Yanick Senn</span>
          <div className="flex items-center gap-6">
            <a
              href="https://github.com/yanicksenn"
              target="_blank"
              rel="noreferrer"
              className="text-zinc-400 hover:text-white transition-colors"
            >
              GitHub
            </a>
            <a
              href="mailto:info@yanicksenn.com"
              className="text-zinc-400 hover:text-white transition-colors"
            >
              Email
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
